import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Avatar,
  Chip,
  Rating,
  Divider,
  IconButton,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Breadcrumbs,
  Link,
  Alert,
  Tabs,
  Tab,
  Badge,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Store as StoreIcon,
  Verified as VerifiedIcon,
  Star as StarIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as WebsiteIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Inventory as InventoryIcon,
  ThumbUp as ThumbUpIcon,
  Share as ShareIcon,
  Message as MessageIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  LocalShipping as ShippingIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

// Mock seller data
const mockSeller = {
  id: 'seller1',
  name: 'EcoWear Store',
  avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop&crop=face',
  coverImage: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1200&h=400&fit=crop',
  description: 'EcoWear is committed to sustainable fashion. We create high-quality, organic clothing that\'s good for you and the planet. Our mission is to provide eco-friendly alternatives to fast fashion while maintaining style and comfort.',
  rating: 4.8,
  reviewCount: 1250,
  totalSales: 1250,
  joinDate: '2023-01-15',
  verified: true,
  location: 'San Francisco, CA',
  responseTime: 'Usually responds within 2 hours',
  website: 'https://ecowear.com',
  email: 'contact@ecowear.com',
  phone: '+1 (555) 123-4567',
  categories: ['Clothing', 'Accessories', 'Shoes'],
  specialties: ['Organic Cotton', 'Sustainable Fashion', 'Eco-Friendly'],
  policies: {
    shipping: 'Free shipping on orders over $50',
    returns: '30-day return policy',
    warranty: '1-year quality guarantee',
  },
  stats: {
    totalProducts: 45,
    activeProducts: 42,
    totalOrders: 1250,
    repeatCustomers: 68,
    averageRating: 4.8,
    responseRate: 98,
  },
};

// Mock seller products
const mockSellerProducts = [
  {
    id: '1',
    title: 'Premium Cotton T-Shirt',
    price: 29.99,
    originalPrice: 39.99,
    image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=300&h=300&fit=crop',
    rating: 4.5,
    reviewCount: 12,
    sold: 23,
    stock: 45,
    category: 'Clothing',
    tags: ['casual', 'cotton', 'comfortable'],
  },
  {
    id: '2',
    title: 'Designer Denim Jeans',
    price: 89.99,
    originalPrice: 119.99,
    image: 'https://images.unsplash.com/photo-1542272604-787c3835535d?w=300&h=300&fit=crop',
    rating: 4.8,
    reviewCount: 8,
    sold: 15,
    stock: 28,
    category: 'Clothing',
    tags: ['denim', 'casual', 'premium'],
  },
  {
    id: '3',
    title: 'Elegant Summer Dress',
    price: 65.99,
    originalPrice: 85.99,
    image: 'https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=300&h=300&fit=crop',
    rating: 4.6,
    reviewCount: 5,
    sold: 8,
    stock: 12,
    category: 'Clothing',
    tags: ['summer', 'elegant', 'floral'],
  },
  {
    id: '4',
    title: 'Leather Crossbody Bag',
    price: 79.99,
    originalPrice: 99.99,
    image: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=300&h=300&fit=crop',
    rating: 4.9,
    reviewCount: 18,
    sold: 12,
    stock: 8,
    category: 'Accessories',
    tags: ['leather', 'luxury', 'handbag'],
  },
  {
    id: '5',
    title: 'Organic Cotton Hoodie',
    price: 49.99,
    originalPrice: 69.99,
    image: 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=300&h=300&fit=crop',
    rating: 4.7,
    reviewCount: 15,
    sold: 32,
    stock: 18,
    category: 'Clothing',
    tags: ['hoodie', 'organic', 'comfortable'],
  },
  {
    id: '6',
    title: 'Sustainable Sneakers',
    price: 99.99,
    originalPrice: 129.99,
    image: 'https://images.unsplash.com/photo-1549298916-b41d501d3772?w=300&h=300&fit=crop',
    rating: 4.4,
    reviewCount: 7,
    sold: 5,
    stock: 15,
    category: 'Shoes',
    tags: ['sneakers', 'sustainable', 'comfortable'],
  },
];

// Mock seller reviews
const mockSellerReviews = [
  {
    id: 1,
    user: {
      name: 'Sarah Johnson',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100&h=100&fit=crop&crop=face',
      verified: true,
    },
    product: 'Premium Cotton T-Shirt',
    rating: 5,
    date: '2024-01-22',
    title: 'Amazing quality and fast shipping!',
    comment: 'Love this t-shirt! The cotton is so soft and comfortable. The seller was very responsive and shipping was super fast. Will definitely order more from this store.',
    helpful: 8,
  },
  {
    id: 2,
    user: {
      name: 'Mike Chen',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
      verified: false,
    },
    product: 'Designer Denim Jeans',
    rating: 4,
    date: '2024-01-20',
    title: 'Great quality, excellent customer service',
    comment: 'The jeans are exactly as described. The seller was very helpful when I had questions about sizing. Highly recommend this store!',
    helpful: 5,
  },
  {
    id: 3,
    user: {
      name: 'Emily Davis',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face',
      verified: true,
    },
    product: 'Elegant Summer Dress',
    rating: 5,
    date: '2024-01-18',
    title: 'Perfect dress and amazing seller!',
    comment: 'This dress is absolutely beautiful and the quality is outstanding. The seller was so helpful and even included a handwritten note. Will definitely shop here again!',
    helpful: 12,
  },
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`seller-tabpanel-${index}`}
      aria-labelledby={`seller-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const SellerProfilePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [tabValue, setTabValue] = useState(0);

  const seller = mockSeller; // In real app, fetch by id

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const renderSellerHeader = () => (
    <Box sx={{ position: 'relative', mb: 4 }}>
      {/* Cover Image */}
      <Box
        sx={{
          height: { xs: 200, md: 300 },
          backgroundImage: `url(${seller.coverImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          borderRadius: 2,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(transparent, rgba(0,0,0,0.5))',
          }}
        />
      </Box>

      {/* Seller Info Overlay */}
      <Box
        sx={{
          position: 'absolute',
          bottom: { xs: -60, md: -80 },
          left: { xs: 16, md: 24 },
          right: { xs: 16, md: 24 },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'center', md: 'flex-end' },
          gap: 3,
        }}
      >
        <Avatar
          src={seller.avatar}
          sx={{
            width: { xs: 120, md: 160 },
            height: { xs: 120, md: 160 },
            border: '4px solid white',
            boxShadow: 3,
          }}
        />
        
        <Box sx={{ flex: 1, textAlign: { xs: 'center', md: 'left' } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, justifyContent: { xs: 'center', md: 'flex-start' } }}>
            <Typography 
              variant="h4" 
              fontWeight="bold" 
              sx={{ 
                color: '#1a1a1a',
                textShadow: '1px 1px 2px rgba(255,255,255,0.8), -1px -1px 2px rgba(255,255,255,0.8)',
                filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.6))'
              }}
            >
              {seller.name}
            </Typography>
            {seller.verified && (
              <VerifiedIcon 
                sx={{ 
                  color: 'primary.main', 
                  fontSize: 32,
                  filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.6))'
                }} 
              />
            )}
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, justifyContent: { xs: 'center', md: 'flex-start' } }}>
            <Rating 
              value={seller.rating} 
              precision={0.1} 
              size="large" 
              readOnly 
              sx={{
                '& .MuiRating-icon': {
                  filter: 'drop-shadow(0 0 2px rgba(255,255,255,0.8))',
                  textShadow: '1px 1px 2px rgba(255,255,255,0.8)'
                },
                '& .MuiRating-iconEmpty': {
                  color: 'rgba(255,255,255,0.3)',
                  filter: 'drop-shadow(0 0 2px rgba(255,255,255,0.4))'
                }
              }}
            />
            <Typography 
              variant="h6" 
              fontWeight="bold"
              sx={{ 
                color: '#1a1a1a',
                textShadow: '1px 1px 2px rgba(255,255,255,0.8), -1px -1px 2px rgba(255,255,255,0.8)'
              }}
            >
              {seller.rating} ({seller.reviewCount} reviews)
            </Typography>
          </Box>
          
          <Typography 
            variant="body1" 
            sx={{ 
              color: '#2a2a2a',
              textShadow: '1px 1px 2px rgba(255,255,255,0.7), -1px -1px 2px rgba(255,255,255,0.7)',
              fontWeight: 500
            }}
          >
            {seller.location} • {seller.totalSales} sales
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'row', md: 'column' } }}>
          <Button
            variant="contained"
            startIcon={<MessageIcon />}
            sx={{ 
              bgcolor: 'primary.main',
              color: 'white',
              px: 3,
              py: 1.5,
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 'bold',
              fontSize: '1rem',
              boxShadow: 3,
              '&:hover': { 
                bgcolor: 'primary.dark',
                boxShadow: 4,
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            Contact Seller
          </Button>
          <IconButton
            sx={{ 
              bgcolor: 'white',
              color: 'text.secondary',
              boxShadow: 2,
              width: 48,
              height: 48,
              borderRadius: '50%',
              '&:hover': { 
                bgcolor: 'grey.100',
                boxShadow: 3,
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            <ShareIcon />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );

  const renderSellerStats = () => (
    <Card sx={{ mb: 4, borderRadius: 2, boxShadow: 1 }}>
      <CardContent>
        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
          Store Statistics
        </Typography>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          <Box sx={{ textAlign: 'center', flex: '1 1 150px' }}>
            <Typography variant="h4" fontWeight="bold" color="primary.main">
              {seller.stats.totalProducts}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Products
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center', flex: '1 1 150px' }}>
            <Typography variant="h4" fontWeight="bold" color="success.main">
              {seller.stats.totalOrders}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Orders
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center', flex: '1 1 150px' }}>
            <Typography variant="h4" fontWeight="bold" color="info.main">
              {seller.stats.repeatCustomers}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Repeat Customers
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center', flex: '1 1 150px' }}>
            <Typography variant="h4" fontWeight="bold" color="warning.main">
              {seller.stats.responseRate}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Response Rate
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const renderSellerInfo = () => (
    <Card sx={{ mb: 4, borderRadius: 2, boxShadow: 1 }}>
      <CardContent>
        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
          About {seller.name}
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
          {seller.description}
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationIcon color="action" />
            <Typography variant="body2">{seller.location}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarIcon color="action" />
            <Typography variant="body2">Joined {new Date(seller.joinDate).toLocaleDateString()}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon color="action" />
            <Typography variant="body2">{seller.responseTime}</Typography>
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Specialties
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {seller.specialties.map((specialty, index) => (
              <Chip
                key={index}
                label={specialty}
                color="primary"
                variant="outlined"
                size="small"
              />
            ))}
          </Box>
        </Box>

        <Box>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Store Policies
          </Typography>
          <List dense>
            <ListItem sx={{ px: 0 }}>
              <ListItemIcon>
                <ShippingIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary={seller.policies.shipping} />
            </ListItem>
            <ListItem sx={{ px: 0 }}>
              <ListItemIcon>
                <CheckIcon color="success" />
              </ListItemIcon>
              <ListItemText primary={seller.policies.returns} />
            </ListItem>
            <ListItem sx={{ px: 0 }}>
              <ListItemIcon>
                <SecurityIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary={seller.policies.warranty} />
            </ListItem>
          </List>
        </Box>
      </CardContent>
    </Card>
  );


  const renderReviews = () => (
    <Box>
      <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
        Customer Reviews
      </Typography>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {mockSellerReviews.map((review) => (
          <Card key={review.id} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar src={review.user.avatar} />
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {review.user.name}
                    </Typography>
                    {review.user.verified && (
                      <VerifiedIcon color="primary" fontSize="small" />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Rating value={review.rating} size="small" readOnly />
                    <Typography variant="body2" color="text.secondary">
                      {new Date(review.date).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Review for: {review.product}
              </Typography>
              
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                {review.title}
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 2 }}>
                {review.comment}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Was this helpful?
                </Typography>
                <IconButton size="small">
                  <ThumbUpIcon fontSize="small" />
                </IconButton>
                <Typography variant="body2" color="text.secondary">
                  {review.helpful}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Back Button */}
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 3 }}
        >
          Back
        </Button>

        {/* Seller Header */}
        {renderSellerHeader()}

        {/* Spacer for header overlap */}
        <Box sx={{ height: { xs: 80, md: 100 } }} />

        {/* Seller Stats */}
        {renderSellerStats()}

        {/* Seller Info */}
        {renderSellerInfo()}

        {/* Products Section */}
        <Card sx={{ mb: 4, borderRadius: 2, boxShadow: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Products by {seller.name}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Discover our collection of {seller.stats.totalProducts} quality products
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="large"
                startIcon={<StoreIcon />}
                sx={{ px: 3, py: 1.5 }}
              >
                View All Products
              </Button>
            </Box>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              {mockSellerProducts.slice(0, 6).map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <Card
                    sx={{
                      width: 280,
                      borderRadius: 2,
                      boxShadow: 1,
                      cursor: 'pointer',
                      '&:hover': { 
                        boxShadow: 3,
                        transform: 'translateY(-2px)',
                        transition: 'all 0.3s ease-in-out'
                      },
                    }}
                    onClick={() => navigate(`/product/${product.id}`)}
                  >
                    <Box sx={{ position: 'relative' }}>
                      <Box
                        component="img"
                        src={product.image}
                        alt={product.title}
                        sx={{
                          width: '100%',
                          height: 200,
                          objectFit: 'cover',
                          borderRadius: '8px 8px 0 0',
                        }}
                      />
                      {product.originalPrice && (
                        <Chip
                          label={`${Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)}% OFF`}
                          color="error"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            fontWeight: 'bold',
                          }}
                        />
                      )}
                    </Box>
                    
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {product.title}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="h6" color="primary.main" fontWeight="bold">
                          ${product.price}
                        </Typography>
                        {product.originalPrice && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ textDecoration: 'line-through' }}
                          >
                            ${product.originalPrice}
                          </Typography>
                        )}
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Rating value={product.rating} precision={0.1} size="small" readOnly />
                        <Typography variant="body2" color="text.secondary">
                          ({product.reviewCount})
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <Chip
                          label={`${product.sold} sold`}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                        <Chip
                          label={product.category}
                          size="small"
                          variant="outlined"
                        />
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          {product.stock} in stock
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {product.tags.slice(0, 2).map((tag, idx) => (
                            <Chip
                              key={idx}
                              label={tag}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          ))}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </Box>

            {mockSellerProducts.length > 6 && (
              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<StoreIcon />}
                  sx={{ px: 4, py: 1.5 }}
                >
                  View All {seller.stats.totalProducts} Products
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Tabs Section */}
        <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Reviews" />
            <Tab label="About" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            {renderReviews()}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Contact Information
                </Typography>
                <List>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon>
                      <EmailIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={seller.email} />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon>
                      <PhoneIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={seller.phone} />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon>
                      <WebsiteIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={seller.website} />
                  </ListItem>
                </List>
              </Box>

              <Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Store Categories
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {seller.categories.map((category, index) => (
                    <Chip
                      key={index}
                      label={category}
                      color="secondary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          </TabPanel>
        </Card>
      </Container>
    </Box>
  );
};

export default SellerProfilePage;
